/**
 * CollegeCard - Individual College Card Component
 * Shows college name, deadline, progress, and expandable task list
 */

import React from 'react';
import { School, ChevronDown, ChevronRight, Calendar, AlertCircle, CheckCircle2, Target } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import SubtaskItem from './SubtaskItem';
import { isOverdue, getDaysUntilDue, formatDueDate } from '../../utils/taskUtils';

const CollegeCard = ({
  college,
  stats,
  tasks = [],
  isExpanded = false,
  onToggle,
  onTaskClick,
  onTaskComplete,
  onAISupport,
  className = ''
}) => {
  // Get the primary deadline for this college
  const getDeadline = () => {
    if (!college.global_colleges) return null;
    return college.global_colleges.ed_deadline ||
           college.global_colleges.ea_deadline ||
           college.global_colleges.rd_deadline;
  };

  const deadline = getDeadline();
  const deadlineDate = deadline ? parseISO(deadline) : null;
  const daysUntil = deadlineDate ? differenceInDays(deadlineDate, new Date()) : null;

  // Determine deadline type
  const getDeadlineType = () => {
    if (!college.global_colleges) return '';
    if (college.global_colleges.ed_deadline) return 'ED';
    if (college.global_colleges.ea_deadline) return 'EA';
    if (college.global_colleges.rd_deadline) return 'RD';
    return '';
  };

  const deadlineType = getDeadlineType();

  // Determine urgency styling
  const getUrgencyStyles = () => {
    if (daysUntil === null) return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' };
    if (daysUntil < 0) return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' };
    if (daysUntil <= 7) return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' };
    if (daysUntil <= 30) return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' };
    return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
  };

  const urgencyStyles = getUrgencyStyles();

  // Determine completion status styling
  const isFullyComplete = stats.percentage === 100;
  const cardBg = isFullyComplete ? 'bg-gray-50' : 'bg-white';
  const cardBorder = isFullyComplete
    ? 'border-gray-300'
    : stats.overdue > 0
    ? 'border-red-300'
    : 'border-gray-200';

  // Sort tasks by due date and status
  const sortedTasks = [...tasks].sort((a, b) => {
    // Completed tasks go to bottom
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;

    // Sort incomplete tasks by overdue, then due date
    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    if (a.due_date && b.due_date) {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    return 0;
  });

  return (
    <div className={`${cardBg} rounded-lg border-2 ${cardBorder} shadow-sm overflow-hidden ${className}`}>
      {/* Header - Clickable to expand/collapse */}
      <div
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* College Icon */}
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center mt-1">
            <School className="w-5 h-5" />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* College Name and Deadline */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${isFullyComplete ? 'text-gray-500' : 'text-gray-900'}`}>
                  {college.name}
                </h3>
                {deadline && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${urgencyStyles.bg} ${urgencyStyles.text}`}>
                      <Calendar className="w-3 h-3" />
                      {deadlineType} {format(deadlineDate, 'MMM d, yyyy')}
                    </span>
                    {daysUntil !== null && daysUntil >= 0 && (
                      <span className="text-xs text-gray-600">
                        ({daysUntil} day{daysUntil !== 1 ? 's' : ''} away)
                      </span>
                    )}
                    {daysUntil !== null && daysUntil < 0 && (
                      <span className="text-xs text-red-600 font-medium">
                        ({Math.abs(daysUntil)} day{Math.abs(daysUntil) !== 1 ? 's' : ''} overdue)
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Expand/Collapse Icon */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>
                  {stats.completed} of {stats.total} task{stats.total !== 1 ? 's' : ''} complete
                </span>
                <span className="font-medium">{stats.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    stats.percentage === 100
                      ? 'bg-green-500'
                      : 'bg-indigo-500'
                  }`}
                  style={{ width: `${stats.percentage}%` }}
                />
              </div>
            </div>

            {/* Stats and Badges Row */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Overdue Badge */}
              {stats.overdue > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  {stats.overdue} overdue
                </span>
              )}

              {/* Completed Badge */}
              {isFullyComplete && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  All complete!
                </span>
              )}

              {/* Next Task Preview (when collapsed) */}
              {!isExpanded && stats.nextTask && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  <Target className="w-3 h-3" />
                  Next: {stats.nextTask.title.substring(0, 30)}{stats.nextTask.title.length > 30 ? '...' : ''}
                </span>
              )}

              {/* Category Breakdown (when collapsed) */}
              {!isExpanded && Object.keys(stats.categoryBreakdown).length > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  {Object.entries(stats.categoryBreakdown).slice(0, 3).map(([category, counts]) => (
                    <span key={category} className="capitalize">
                      {category}: {counts.completed}/{counts.total}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Task List */}
      {isExpanded && tasks.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50">
          {/* Category Breakdown Header */}
          {Object.keys(stats.categoryBreakdown).length > 1 && (
            <div className="px-4 py-3 bg-white border-b border-gray-200">
              <div className="flex items-center gap-4 flex-wrap text-sm">
                <span className="font-medium text-gray-700">Progress by category:</span>
                {Object.entries(stats.categoryBreakdown).map(([category, counts]) => (
                  <span key={category} className="text-gray-600 capitalize">
                    <span className="font-medium">{category}:</span> {counts.completed}/{counts.total}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Task List */}
          <div className="px-4 py-3 space-y-2">
            {sortedTasks.map((task) => (
              <SubtaskItem
                key={task.id}
                subtask={task}
                onComplete={onTaskComplete}
                onAISupport={onAISupport}
                onTaskClick={onTaskClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeCard;
